import axios from "axios";
import { QueryKey, useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { Error, PostVariables, QueryDataItem } from "./types";

import { getQueryKey } from "@/helpers";

export type UsePostOptions<TData = unknown, R = Record<string, unknown>> = Partial<
  Omit<UseMutationOptions<TData, Error, PostVariables<R>>, "mutationFn">
>;

interface UsePostProps<TData, R> {
  queryKey?: QueryKey;
  endpoint: string;
  options: UsePostOptions<TData, R>;
}

export function usePost<TData, R = Record<string, unknown>>({
  endpoint,
  queryKey = getQueryKey({ endpoint }),
  options = {},
}: UsePostProps<TData, R>) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, PostVariables<R>>({
    ...options,
    mutationFn: async ({ payload }) => (await axios.post(endpoint, payload)).data,
    onMutate: (variables) => {
      options.onMutate && options.onMutate(variables);
    },
    onSuccess: (data, variables, context) => {
      options.onSuccess && options.onSuccess(data, variables, context);
      const queryData = queryClient.getQueryData<QueryDataItem[]>(queryKey);

      if (Array.isArray(queryData))
        queryClient.setQueryData(queryKey, [...queryData, Array.isArray(data) && data.length === 1 ? data[0] : data]);
    },
    onError: (error, variables, context) => {
      options.onError && options.onError(error, variables, context);
    },
    onSettled: async (data, error, variables, context) => {
      options.onSettled && options.onSettled(data, error, variables, context);
      await queryClient.invalidateQueries({ queryKey: queryKey.slice(0, 1) });
    },
  });
}
